import React, { Dispatch, FC, SetStateAction } from "react";

import { IgTooltip, IgTypography } from "@intelligems/ig-theme";
import { InfoOutlined } from "@mui/icons-material";
import { Grid, IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";

import { useConfiguration } from "../../api/configurations/use-configuration";
import { useGetOrg } from "../../api/organizations/get-organization";
import type {
  BarMetricData,
  HistogramMetricData,
  LineMetricData,
  StackedAreaMetricData,
  StackedBarMetricData,
} from "../../types/analytics";
import Loading from "../../utils/Loading";

import type { GraphConfigGroup } from "./Graphs/Utils/graphConfigs";
import { findGroupedGraphTooltip } from "./Graphs/Utils/graphHelpers";
import {
  headerFontSize,
  headerFontWeight,
} from "./Graphs/Utils/graphStyleCustoms";
import { graphTooltips } from "./Graphs/Utils/graphTooltips";
import { GroupedGraphFilter } from "./Graphs/Utils/GroupedGraphFilter";

const PREFIX = "GraphGroupHeader";

const classes = {
  graphHeader: `${PREFIX}-graphHeader`,
  impactNumber: `${PREFIX}-impactNumber`,
  aovFilterSelect: `${PREFIX}-aovFilterSelect`,
  graphHeaderFlex: `${PREFIX}-graphHeaderFlex`,
};

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`& .${classes.impactNumber}`]: {
    fontSize: "35px",
    fontWeight: 600,
    letterSpacing: ".15px",
  },
  [`& .${classes.graphHeader}`]: {
    fontWeight: headerFontWeight,
    fontSize: headerFontSize,
  },
  [`& .${classes.aovFilterSelect}`]: {
    height: "25px",
  },
  [`& .${classes.graphHeaderFlex}`]: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginLeft: "20px",
    marginBottom: "10px",
  },
}));

interface GroupGraphHeaderProps {
  groupMetricData:
    | BarMetricData[]
    | HistogramMetricData[]
    | StackedBarMetricData[]
    | StackedAreaMetricData[];
  config: GraphConfigGroup;
  filterMethod?: string;
  setFilterMethod?: Dispatch<SetStateAction<string>>;
  filterList?: { id: string; name: string }[];
}

export const GroupGraphHeader: FC<GroupGraphHeaderProps> = ({
  groupMetricData,
  config,
  filterMethod,
  setFilterMethod,
  filterList,
}) => {
  const organization = useGetOrg();
  const shouldShowProfit = useConfiguration<boolean>(
    "should_show_profit_in_dashboard"
  );

  if (shouldShowProfit?.value !== undefined) {
    return (
      <StyledGrid
        container
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Grid item sx={{ width: "20px" }} />
        <Grid item>
          <div className={classes.graphHeaderFlex}>
            {config.showGroupTitle && (
              <>
                <IgTypography className={classes.graphHeader}>
                  {config.groupTitle}
                </IgTypography>
                {groupMetricData &&
                  findGroupedGraphTooltip(groupMetricData, graphTooltips) !==
                    null && (
                    <IgTooltip
                      title={
                        <p>
                          {findGroupedGraphTooltip(
                            groupMetricData,
                            graphTooltips
                          )}
                        </p>
                      }
                    >
                      <IconButton size={"small"} aria-label="info">
                        <InfoOutlined fontSize={"small"} />
                      </IconButton>
                    </IgTooltip>
                  )}
              </>
            )}
          </div>
        </Grid>
        <Grid item>
          {config.showFilter && filterMethod && setFilterMethod && (
            <GroupedGraphFilter
              data={groupMetricData as BarMetricData[] | LineMetricData[]}
              filterChoice={filterMethod!}
              setFilterChoice={setFilterMethod!}
              filterListSubheader={config.filterListSubheader}
              config={config}
              filterList={filterList}
            />
          )}
        </Grid>
      </StyledGrid>
    );
  }
  return <Loading />;
};
